import { useState, useEffect, useCallback, useRef } from 'react';
import { supportService } from '../services/support';
import {
  Ticket,
  TicketDetail,
  Message,
  SupportCall,
  TicketQueryParams,
} from '../types/support';
import { showToast } from '../utils/helpers';

// ============================================================================
// useTickets - Fetch paginated ticket list with filters
// ============================================================================

interface UseTicketsState {
  tickets: Ticket[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

interface UseTicketsReturn extends UseTicketsState {
  refetch: () => Promise<void>;
  setPage: (page: number) => void;
}

export function useTickets(params: TicketQueryParams): UseTicketsReturn {
  const [state, setState] = useState<UseTicketsState>({
    tickets: [],
    total: 0,
    page: params.page || 1,
    pageSize: params.pageSize || 20,
    totalPages: 0,
    loading: true,
    error: null,
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchTickets = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await supportService.getTickets(paramsRef.current);
      setState({
        tickets: response.data,
        total: response.total,
        page: response.page,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        loading: false,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch tickets';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets, params.status, params.priority, params.category, params.search, params.page]);

  const setPage = useCallback((newPage: number) => {
    paramsRef.current = { ...paramsRef.current, page: newPage };
    fetchTickets();
  }, [fetchTickets]);

  return {
    ...state,
    refetch: fetchTickets,
    setPage,
  };
}

// ============================================================================
// useTicketDetail - Fetch single ticket with full details
// ============================================================================

interface UseTicketDetailState {
  ticket: TicketDetail | null;
  loading: boolean;
  error: string | null;
}

interface UseTicketDetailReturn extends UseTicketDetailState {
  refetch: () => Promise<void>;
  updateTicket: (updates: Partial<TicketDetail>) => void;
}

export function useTicketDetail(ticketId: string | null): UseTicketDetailReturn {
  const [state, setState] = useState<UseTicketDetailState>({
    ticket: null,
    loading: false,
    error: null,
  });

  const fetchTicket = useCallback(async () => {
    if (!ticketId) {
      setState({ ticket: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const ticket = await supportService.getTicketById(ticketId);
      setState({ ticket, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch ticket';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [ticketId]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  const updateTicket = useCallback((updates: Partial<TicketDetail>) => {
    setState((prev) => ({
      ...prev,
      ticket: prev.ticket ? { ...prev.ticket, ...updates } : null,
    }));
  }, []);

  return {
    ...state,
    refetch: fetchTicket,
    updateTicket,
  };
}

// ============================================================================
// useMessages - Fetch and poll messages for a ticket
// ============================================================================

interface UseMessagesState {
  messages: Message[];
  loading: boolean;
  error: string | null;
}

interface UseMessagesReturn extends UseMessagesState {
  refetch: () => Promise<void>;
  addLocalMessage: (message: Message) => void;
}

export function useMessages(
  ticketId: string | null,
  pollInterval: number = 5000
): UseMessagesReturn {
  const [state, setState] = useState<UseMessagesState>({
    messages: [],
    loading: false,
    error: null,
  });

  const lastMessageIdRef = useRef<string | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchMessages = useCallback(async (isPolling = false) => {
    if (!ticketId) {
      setState({ messages: [], loading: false, error: null });
      return;
    }

    if (!isPolling) {
      setState((prev) => ({ ...prev, loading: true, error: null }));
    }

    try {
      const params = isPolling && lastMessageIdRef.current
        ? { afterMessageId: lastMessageIdRef.current }
        : {};
      
      const response = await supportService.getMessages(ticketId, params);
      
      if (isPolling && response.data.length > 0) {
        // Append new messages
        setState((prev) => ({
          ...prev,
          messages: [...prev.messages, ...response.data],
          loading: false,
          error: null,
        }));
      } else if (!isPolling) {
        setState({
          messages: response.data,
          loading: false,
          error: null,
        });
      }

      // Track last message for polling
      if (response.data.length > 0) {
        lastMessageIdRef.current = response.data[response.data.length - 1].id;
      }
    } catch (err) {
      if (!isPolling) {
        const message = err instanceof Error ? err.message : 'Failed to fetch messages';
        setState((prev) => ({ ...prev, loading: false, error: message }));
      }
    }
  }, [ticketId]);

  // Initial fetch
  useEffect(() => {
    lastMessageIdRef.current = null;
    fetchMessages(false);
  }, [fetchMessages, ticketId]);

  // Polling
  useEffect(() => {
    if (!ticketId || pollInterval <= 0) return;

    pollIntervalRef.current = setInterval(() => {
      fetchMessages(true);
    }, pollInterval);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [ticketId, pollInterval, fetchMessages]);

  const addLocalMessage = useCallback((message: Message) => {
    setState((prev) => ({
      ...prev,
      messages: [...prev.messages, message],
    }));
    lastMessageIdRef.current = message.id;
  }, []);

  return {
    ...state,
    refetch: () => fetchMessages(false),
    addLocalMessage,
  };
}

// ============================================================================
// useCallHistory - Fetch calls for a ticket
// ============================================================================

interface UseCallHistoryState {
  calls: SupportCall[];
  loading: boolean;
  error: string | null;
}

export function useCallHistory(ticketId: string | null): UseCallHistoryState & { refetch: () => Promise<void> } {
  const [state, setState] = useState<UseCallHistoryState>({
    calls: [],
    loading: false,
    error: null,
  });

  const fetchCalls = useCallback(async () => {
    if (!ticketId) {
      setState({ calls: [], loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const calls = await supportService.getCallsForTicket(ticketId);
      setState({ calls, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch calls';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [ticketId]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  return {
    ...state,
    refetch: fetchCalls,
  };
}

// ============================================================================
// useTicketActions - Ticket update mutations
// ============================================================================

export function useTicketActions() {
  const [loading, setLoading] = useState(false);

  const updateStatus = useCallback(async (ticketId: string, status: string) => {
    setLoading(true);
    try {
      await supportService.updateTicket(ticketId, { status: status as any });
      showToast('success', 'Ticket status updated');
      return true;
    } catch (err) {
      showToast('error', 'Failed to update ticket status');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePriority = useCallback(async (ticketId: string, priority: string) => {
    setLoading(true);
    try {
      await supportService.updateTicket(ticketId, { priority: priority as any });
      showToast('success', 'Ticket priority updated');
      return true;
    } catch (err) {
      showToast('error', 'Failed to update priority');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const assignTicket = useCallback(async (ticketId: string, agentId: string | null) => {
    setLoading(true);
    try {
      await supportService.updateTicket(ticketId, { assignedToUserId: agentId });
      showToast('success', agentId ? 'Ticket assigned' : 'Ticket unassigned');
      return true;
    } catch (err) {
      showToast('error', 'Failed to assign ticket');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const escalate = useCallback(async (ticketId: string, reason: string) => {
    setLoading(true);
    try {
      await supportService.escalateTicket(ticketId, {
        reason,
        upgradePriorityToUrgent: true,
      });
      showToast('success', 'Ticket escalated for admin review');
      return true;
    } catch (err) {
      showToast('error', 'Failed to escalate ticket');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendMessage = useCallback(async (
    ticketId: string,
    content: string,
    type: 'APP' | 'INTERNAL_NOTE'
  ) => {
    setLoading(true);
    try {
      const message = await supportService.addMessage(ticketId, { content, type });
      showToast('success', type === 'APP' ? 'Reply sent' : 'Note added');
      return message;
    } catch (err) {
      showToast('error', 'Failed to send message');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    updateStatus,
    updatePriority,
    assignTicket,
    escalate,
    sendMessage,
  };
}
